import type { GameSummary, SlotScore } from "../../shared/types";
import type { CharacterColor } from "../components/StockIcon";

export interface EntrantInfo {
  port: number | null;
  characters: CharacterColor[];
}

/** Return the key with the highest count (ties → first inserted), or null if empty. */
function pickMostCommon<T>(counts: Map<T, number>): T | null {
  let best: T | null = null;
  let bestCount = 0;
  for (const [value, count] of counts) {
    if (count > bestCount) {
      best = value;
      bestCount = count;
    }
  }
  return best;
}

/**
 * Aggregate an entrant's per-game data across the games of a set into a single
 * representative summary: their port and the characters they played (with the
 * most-common color per character; ties → first-seen). Match games to the
 * entrant via their slippi displayName matching one of slot.displayNames (the
 * same lookup pattern used for scores).
 */
export function entrantInfo(
  slot: SlotScore | undefined,
  gameSummaries: GameSummary[],
): EntrantInfo {
  if (!slot) return { port: null, characters: [] };
  const slotNames = new Set(slot.displayNames);
  const portCounts = new Map<number, number>();
  const seen = new Map<string, { colors: Map<string, number>; order: number }>();
  let nextOrder = 0;

  for (const summary of gameSummaries) {
    for (const player of summary.players) {
      if (!slotNames.has(player.displayName)) continue;
      portCounts.set(player.port, (portCounts.get(player.port) ?? 0) + 1);
      let entry = seen.get(player.characterName);
      if (!entry) {
        entry = { colors: new Map(), order: nextOrder++ };
        seen.set(player.characterName, entry);
      }
      entry.colors.set(player.characterColor, (entry.colors.get(player.characterColor) ?? 0) + 1);
    }
  }

  const characters = Array.from(seen.entries())
    .sort((a, b) => a[1].order - b[1].order)
    .map(([name, { colors }]) => ({
      name,
      color: pickMostCommon(colors) ?? "Default",
    }));

  return { port: pickMostCommon(portCounts), characters };
}
